import { useRef, useState } from "react";
import { Volume2, Square, Check, Copy } from "lucide-react";
import { toast } from "sonner";

/* Who speaks as Griot.
   These are ElevenLabs' own preview clips, played straight from their public CDN — nothing is
   synthesised here, nothing is spent, no plan is needed to listen. Each one is labelled honestly:
   whether it is a real person's recorded voice or a voice the machine invented, where the speaker
   is actually from, and roughly how old they sound. Listen, then name the one. */

const GROUPS = [
  {
    key: "kenyan",
    title: "Real Kenyan people",
    note: "Recorded from actual speakers who gave their voice to the library. These are the authentic option — but none of them is old.",
    voices: [
      { id: "uLfPT2jUO3X81OwnftBP", name: "Keith Muoki", line: "Kenyan · male · 30s–40s · real person", said: "East African English with a mild Kenyan coastal lilt. Warm baritone, chesty resonance. Used by 4,040 people.", pick: true, url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/4de200f13227453387997e4a59b5a6d2/voices/uLfPT2jUO3X81OwnftBP/cOjdzKLgMXdD7E7jUsFJ.mp3" },
      { id: "f9aL8jTNAvSfAy338u1R", name: "Derrick M", line: "Kenyan · male · young · real person", said: "Smooth, clear East African voice with a Kenyan accent. Built for narration.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/b6eb8f238ca44e0e9761225c5c27d606/voices/f9aL8jTNAvSfAy338u1R/018f15bd-202f-425b-b0a0-30e5285fe2be.mp3" },
      { id: "Tq0wwawNLxDlPBzvUgNg", name: "Tony Kipkemboi", line: "Kenyan-American · male · middle-aged · real person", said: "Kenyan-American narration voice. The accent has travelled.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/b334ff57c7e24322bd589f0f8c7ec329/voices/Tq0wwawNLxDlPBzvUgNg/2198af0e-743d-46ab-8734-0bac3db08e54.mp3" },
      { id: "ijKilL5CnjXKMWDHOJH8", name: "Yna Agalo", line: "Kenyan · female · young · real person", said: "Smooth East African voice, Kenyan accent. 5,389 people use her.", url: "https://storage.googleapis.com/eleven-public-prod/database/user/zhwWAn9mk9N6t0JSzE111FuKdYp1/voices/ijKilL5CnjXKMWDHOJH8/6e1966fc-8c34-4914-8fb8-ad5f66fdeafc.mp3" },
      { id: "hrMENUPSbAbVQk6jkV5k", name: "Maryanne", line: "Kenyan-influenced · female · middle-aged · real person", said: "Warm, soulful, emotionally expressive. Kenyan-influenced global English.", url: "https://storage.googleapis.com/eleven-public-prod/database/user/user_9901kf6510e6f8tsqnsxvb9bq8vg/voices/hrMENUPSbAbVQk6jkV5k/e070abbb-0625-4043-b69f-b8505f9526ce.mp3" },
    ],
  },
  {
    key: "swahili",
    title: "Kiswahili voices — invented, not recorded",
    note: "Nobody is behind these: the machine generated them from a description. They speak Kiswahili natively and carry Nairobi or Dar es Salaam cadence. No age is declared, and none sounds seventy.",
    voices: [
      { id: "h65Hy3wCmpuo1Fce3en3", name: "Juma — calm, resonant", line: "Kiswahili · male · Nairobi cadence · synthetic", said: "Low resonant timbre, slow deliberate pace, long pauses between lines. The closest thing here to an elder.", pick: true, url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/1da06ea679a54975ad96a2221fe6530d/voices/h65Hy3wCmpuo1Fce3en3/8pLlDOiKPaH1rhy6NU5i.mp3" },
      { id: "f07nEXP6Cp3SGtqJtJfh", name: "Rashidi — amused, blunt", line: "Kiswahili · male · Dar es Salaam · synthetic", said: "Thin, raspy, fast. Cuts his lines short. Blunt humour with an edge.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/1da06ea679a54975ad96a2221fe6530d/voices/f07nEXP6Cp3SGtqJtJfh/3yeflR4q2IAWofppjLyw.mp3" },
      { id: "90GgjYd3t15Qldd8eZuQ", name: "Baraka — playful", line: "Kiswahili · male · Dar es Salaam · synthetic", said: "Light raspy energy, bounces through a line, self-assured. The trickster register.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/1da06ea679a54975ad96a2221fe6530d/voices/90GgjYd3t15Qldd8eZuQ/1DmVaUcG1NISazaeRB1m.mp3" },
      { id: "85I9qUNaRIEQGWJ91dHe", name: "Juma — blunt, amused", line: "Kiswahili · male · synthetic", said: "Clipped, raspy, openly amused by his own bluntness.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/1da06ea679a54975ad96a2221fe6530d/voices/85I9qUNaRIEQGWJ91dHe/f9AKM0Apl8SuZ2scp3hk.mp3" },
      { id: "i5oE89JoUCpIgvSOemWx", name: "Halima — warm, curious", line: "Kiswahili · female · Tanga cadence · synthetic", said: "Mellow and low, genuine curiosity, softens at the end of a sentence.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/1da06ea679a54975ad96a2221fe6530d/voices/i5oE89JoUCpIgvSOemWx/uKXrE83WKgPrsmwMkHhQ.mp3" },
    ],
  },
  {
    key: "elders",
    title: "Men who actually sound seventy — but not Kenyan",
    note: "If age in the voice matters more than the exact nation, these are real elders. Say so plainly to participants if we use one; a South African elder is not a Nairobi elder.",
    voices: [
      { id: "L5zW3PqYZoWAeS4J1qMV", name: "Dr. Samuel Rosso", line: "South African · male · over 70 · real person", said: "A South African man past seventy. Distinguished, clever, unhurried. 5,048 people use him.", pick: true, url: "https://storage.googleapis.com/eleven-public-prod/database/user/zHoYoXsWO3RKGjCJ3I6kACxGeFF3/voices/L5zW3PqYZoWAeS4J1qMV/7cea2917-f48c-497b-be67-420f4cc1d944.mp3" },
      { id: "qqFmXQ8vFdN4b2o5oHN9", name: "Tunji", line: "African / British · male · old · real person", said: "African with bold hints of British English. Calm, technical, podcast register.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/c6084e0943524cf39b7fc79575fe9a43/voices/qqFmXQ8vFdN4b2o5oHN9/43c86e60-86c3-4fc4-afb7-26346bccf287.mp3" },
      { id: "uDIZibXFTMARZYTPJhMF", name: "Ghanaba", line: "West African · male · old · real person", said: "An African voice recorded for lectures. Measured, authoritative.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/54f4acba7a454f20a0a63a9dd77b9b74/voices/uDIZibXFTMARZYTPJhMF/32acc0f4-1d8c-4af0-867c-9e48a4ae7214.mp3" },
      { id: "VJBe5pHkFEQshs0TwwMB", name: "Thomas Patterson", line: "African-American · male · old · real person", said: "Mature storyteller, midwest roots. Porch-step cadence.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/6ed1367ddca94f128229aeb3cd330317/voices/VJBe5pHkFEQshs0TwwMB/DQoqD4nBbfP2cnN2Uy1b.mp3" },
      { id: "j0x5aBWfr9KRRExq1lTf", name: "Jean Maurice", line: "African · male · old · real person · French", said: "Warm, deep, authentic. His recorded language is French — English would be a stretch.", url: "https://storage.googleapis.com/eleven-public-prod/database/workspace/871d103ece2c432182b53977a10e3369/voices/j0x5aBWfr9KRRExq1lTf/74b9ba6a-f52f-4dcc-89fd-6041969d6703.mp3" },
    ],
  },
];

const Row = ({ v, playing, onPlay, chosen, onChoose }) => (
  <div
    data-testid={`audition-row-${v.id}`}
    className="flex items-start gap-3 border-b border-white/[0.06] py-4 last:border-0"
    style={chosen ? { borderLeft: "2px solid #E07A5F", paddingLeft: 14, marginLeft: -16 } : undefined}
  >
    <button
      data-testid={`audition-play-${v.id}`}
      onClick={() => onPlay(v)}
      aria-label={`Hear ${v.name}`}
      className="mt-0.5 grid h-11 w-11 shrink-0 place-items-center rounded-full border border-[#E07A5F]/45 text-[#F4A261] transition-colors hover:border-[#E07A5F] hover:bg-[#E07A5F]/12"
    >
      {playing === v.id ? <Square size={13} /> : <Volume2 size={16} />}
    </button>
    <div className="min-w-0 flex-1">
      <p className="flex flex-wrap items-baseline gap-x-2 text-[17px] leading-tight text-[#F4EFE6]">
        {v.name}
        {v.pick && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#E07A5F]">my pick here</span>}
      </p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.1em] text-[#6E7582]">{v.line}</p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-[#A1A8B3]">{v.said}</p>
    </div>
    <button
      data-testid={`audition-choose-${v.id}`}
      onClick={() => onChoose(v)}
      className="mt-0.5 shrink-0 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors"
      style={chosen
        ? { borderColor: "#E07A5F", color: "#F4A261", background: "rgba(224,122,95,0.12)" }
        : { borderColor: "rgba(255,255,255,0.12)", color: "#A1A8B3" }}
    >
      {chosen ? <Check size={12} /> : "this one"}
    </button>
  </div>
);

export default function Auditions() {
  const [playing, setPlaying] = useState("");
  const [chosen, setChosen] = useState(null);
  const audio = useRef(null);

  const play = (v) => {
    audio.current?.pause();
    if (playing === v.id) { audio.current = null; setPlaying(""); return; }
    const a = new Audio(v.url);
    audio.current = a;
    setPlaying(v.id);
    a.onended = () => { setPlaying(""); audio.current = null; };
    a.onerror = () => { setPlaying(""); toast.error("That clip would not load"); };
    a.play().catch(() => { setPlaying(""); toast.error("Tap it again — the browser wanted a gesture first"); });
  };

  const choose = (v) => {
    setChosen(v);
    navigator.clipboard?.writeText(`${v.name} — ${v.id}`).catch(() => {});
    toast.success(`${v.name} it is — the name and id are on your clipboard`);
  };

  return (
    <div className="min-h-screen bg-[#0C0C0B] px-6 py-14 text-[#F4EFE6] sm:px-10" data-testid="auditions-page">
      <div className="mx-auto max-w-2xl">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E07A5F]">the voice · auditions</p>
        <h1 className="mt-4 text-4xl leading-[1.05] sm:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Who should speak<br />as the Griot?
        </h1>
        <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-[#A1A8B3]">
          Every clip below is the library's own sample, played from their public files. Nothing is spent
          listening, and no plan is needed. Read the line under each name before you judge the sound — it
          says whether a real person is behind it, where they are from, and how old they are.
        </p>
        <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[#D8D2C4]">
          The honest finding: <em>there is no Kenyan man in his seventies in this library.</em> You can have
          Kenyan and young, Kiswahili and invented, or old and from somewhere else. The fourth way is at the
          bottom.
        </p>

        {GROUPS.map((g) => (
          <section key={g.key} className="mt-14" data-testid={`audition-group-${g.key}`}>
            <h2 className="text-lg text-[#F4EFE6]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{g.title}</h2>
            <p className="mt-1.5 max-w-xl text-[13px] leading-relaxed text-[#6E7582]">{g.note}</p>
            <div className="mt-5">
              {g.voices.map((v) => (
                <Row key={v.id} v={v} playing={playing} onPlay={play} chosen={chosen?.id === v.id} onChoose={choose} />
              ))}
            </div>
          </section>
        ))}

        <section className="mt-16 rounded-2xl border border-[#E07A5F]/25 bg-[#E07A5F]/[0.06] p-6" data-testid="audition-fourth-way">
          <h2 className="text-lg text-[#F4EFE6]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>The fourth way — record the elder</h2>
          <p className="mt-2 text-[14px] leading-relaxed text-[#D8D2C4]">
            One to two minutes of a consenting Nairobi man in his seventies, read into a phone in a quiet
            room, becomes his voice exactly. Not an impression, not a composite — him, credited by name,
            with a release on file. It is the only route that ends with a real Kenyan elder speaking, and it
            costs a voice note and a signature.
          </p>
          <p className="mt-3 font-mono text-[11px] leading-relaxed text-[#A1A8B3]">
            he reads: "Hujambo. Karibu sana, karibu nyumbani. Sit down, my friend, and let the dust settle.
            There were three of them arguing about the thing they had built, and the truth is, neither one
            was wrong. Pole pole — slowly, slowly."
          </p>
        </section>

        {chosen && (
          <div className="sticky bottom-5 mt-10 flex items-center gap-3 rounded-full border border-[#E07A5F]/40 bg-[#121417]/95 px-5 py-3 backdrop-blur" data-testid="audition-chosen-bar">
            <Check size={14} className="shrink-0 text-[#F4A261]" />
            <span className="min-w-0 flex-1 truncate text-[13px] text-[#F4EFE6]">{chosen.name}</span>
            <button
              data-testid="audition-copy-btn"
              onClick={() => { navigator.clipboard?.writeText(`${chosen.name} — ${chosen.id}`); toast.success("Copied"); }}
              className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[#A1A8B3] hover:text-[#F4A261]"
            >
              <Copy size={11} /> copy
            </button>
          </div>
        )}

        <p className="mt-14 border-t border-white/[0.06] pt-6 text-[12px] leading-relaxed text-[#6E7582]">
          Using any of these on the line needs the ElevenLabs Starter plan — the free tier can browse the
          library but refuses to speak with it (I checked: 402, "free users cannot use library voices via
          the API"). Kiswahili also rules out the fast Flash model; the voice would run on v4 Turbo, which
          does speak it, at roughly 150ms to first sound.
        </p>
      </div>
    </div>
  );
}
